import React, { useState } from "react";
import { FileDown, Loader2, Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Btn, Panel } from "@/components/portal/crm/kit";
import { ukDayYear } from "@/lib/time";
import "@/styles/deal-pitch.css";

/** Download the pack — shows progress, because a pack with filed accounts can run to a couple of hundred pages. */
export function PackButton({ onPack, testId, kind = "ghost", label = "Download deal pack" }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await onPack(); toast.success("Deal pack downloaded"); }
    catch { toast.error("Couldn't make the deal pack just now — try again in a moment"); }
    finally { setBusy(false); }
  };
  return (
    <Btn kind={kind} sm onClick={go} disabled={busy} testId={testId}>
      {busy ? <Loader2 className="size-3.5 animate-spin" /> : <FileDown className="size-3.5" />}
      {busy ? "Making the pack…" : label}
    </Btn>
  );
}

const Ask = ({ ask }) => (
  <div className="dp-ask" data-testid="deal-pitch-ask">
    {[["Facility", ask.amount], ["Product", ask.product], ["Term", ask.term ? `${ask.term} months` : "Open"], ["Needed", ask.urgency],
      ["Guarantee", ask.pg === true ? "PG offered" : ask.pg === false ? "No PG" : "To confirm"]].filter(([, v]) => v).map(([k, v]) => (
      <div key={k}><span>{k}</span><b>{v}</b></div>
    ))}
  </div>
);

/** The headline card on a lender desk's case page once the Connexis desk has approved Connie's pitch. */
export function DealPitchCard({ p, onRead, onPack, onSend, testId = "lender-deal-pitch" }) {
  const c = p.content || {};
  return (
    <section className="card dp-card" data-testid={testId}>
      <div className="dp-card-in">
        <div className="dp-eyebrow"><Sparkles className="size-3.5" /> Deal pack · approved by Connexis {ukDayYear(p.approved_at, "")}</div>
        <h3 className="dp-headline" data-testid={`${testId}-headline`}>{c.headline}</h3>
        <p className="dp-summary">{c.summary}</p>
        <Ask ask={p.ask || {}} />
        <div className="dp-actions">
          <Btn kind="primary" sm onClick={onSend} testId={`${testId}-send`}>
            <Mail className="size-3.5" /> {p.broker ? "Email to your lenders" : "Email to credit"}
          </Btn>
          <PackButton onPack={onPack} testId={`${testId}-pack`} />
          <Btn sm onClick={onRead} testId={`${testId}-read`}>Read the pitch</Btn>
        </div>
        <p className="dp-fine">
          One PDF: Connie&apos;s pitch{p.has_check ? ", the Connexis Company Check" : ""}
          {p.docs?.length ? ` and ${p.docs.length} document${p.docs.length === 1 ? "" : "s"} from the case` : ""} — with your name on every page.
        </p>
      </div>
    </section>
  );
}

const Para = ({ title, children, testId }) => (children ? (
  <div className="dp-block" data-testid={testId}><div className="rail-t">{title}</div><p>{children}</p></div>
) : null);

/** The full pitch, as the tab on the lender's case page. */
export function DealPitchView({ p, testId = "lender-pitch" }) {
  const c = p.content || {};
  return (
    <>
      <Panel title="Why it works" sub="The case for lending, from the facts on file" testId={`${testId}-strengths`}>
        <ul className="dp-list">{(c.strengths || []).map((s, i) => <li key={i}><i />{s}</li>)}</ul>
      </Panel>
      <div style={{ height: 16 }} />
      <Panel title="The deal" testId={`${testId}-deal`}>
        <Para title="Use of funds" testId={`${testId}-funds`}>{c.use_of_funds}</Para>
        <Para title="The business" testId={`${testId}-business`}>{c.business}</Para>
        <Para title="Security & guarantees" testId={`${testId}-security`}>{c.security}</Para>
      </Panel>
      {(c.risks || []).length > 0 && (
        <>
          <div style={{ height: 16 }} />
          <Panel title="What to know" sub="What a credit team will spot first — and the answer to each" testId={`${testId}-risks`}>
            <div className="dp-risks">
              {c.risks.map((r, i) => (
                <div key={i} className="dp-risk"><b>{r.risk}</b><span>{r.mitigant}</span></div>
              ))}
            </div>
          </Panel>
        </>
      )}
      {(p.figures || []).length > 0 && (
        <>
          <div style={{ height: 16 }} />
          <Panel title="The numbers" sub="Each figure labelled with where it comes from" testId={`${testId}-numbers`}>
            <div className="dp-figs">
              {p.figures.map((f, i) => (
                <div key={i}><span>{f.label}</span><b>{f.value}</b><small>{f.source}</small></div>
              ))}
            </div>
            {c.numbers_comment && <p className="dp-note">{c.numbers_comment}</p>}
          </Panel>
        </>
      )}
      <div style={{ height: 16 }} />
      <Panel title="What's in the pack" testId={`${testId}-contents`}>
        <ul className="dp-contents">
          <li><b>Deal pitch</b><span>Part 1</span></li>
          {p.has_check && <li><b>Connexis Company Check</b><span>Part 2</span></li>}
          {(p.docs || []).map((d, i) => <li key={i}><b>{d.label}</b><span>{d.filename}</span></li>)}
        </ul>
        {c.ready_for_terms && <p className="dp-note">{c.ready_for_terms}</p>}
      </Panel>
    </>
  );
}
